import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";

// ============================================================================
// SCHEMA
// ============================================================================

const cashflowFormSchema = z.object({
  projectionYear: z
    .number({ invalid_type_error: "Projection year must be a number" })
    .int("Must be a whole number")
    .min(1, "Projection year must be at least 1")
    .max(10, "Projection year cannot exceed 10"),
  periodStartDate: z
    .string()
    .min(1, "Period start date is required"),
  periodEndDate: z
    .string()
    .min(1, "Period end date is required"),
  grossRentalIncome: z
    .string()
    .min(1, "Gross rental income is required"),
  otherIncome: z.string().optional().nullable(),
  vacancyAllowance: z.string().optional().nullable(),
  councilRates: z.string().optional().nullable(),
  waterRates: z.string().optional().nullable(),
  insurance: z.string().optional().nullable(),
  landTax: z.string().optional().nullable(),
  bodyCorporate: z.string().optional().nullable(),
  managementFees: z.string().optional().nullable(),
  repairsMaintenance: z.string().optional().nullable(),
  otherOutgoings: z.string().optional().nullable(),
  capex: z.string().optional().nullable(),
  outgoingsGrowthRate: z.string().optional().nullable(),
  rentGrowthRate: z.string().optional().nullable(),
  interestRateAssumed: z.string().optional().nullable(),
});

type CashflowFormData = z.infer<typeof cashflowFormSchema>;

// ============================================================================
// PROPS
// ============================================================================

interface CashflowFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  securityId: string;
  existingProjection?: any; // undefined = create mode
  suggestedYear?: number;
}

// ============================================================================
// DEFAULT VALUES
// ============================================================================

function buildDefaultValues(suggestedYear: number): CashflowFormData {
  return {
    projectionYear: suggestedYear,
    periodStartDate: "",
    periodEndDate: "",
    grossRentalIncome: "",
    otherIncome: "",
    vacancyAllowance: "",
    councilRates: "",
    waterRates: "",
    insurance: "",
    landTax: "",
    bodyCorporate: "",
    managementFees: "",
    repairsMaintenance: "",
    otherOutgoings: "",
    capex: "",
    outgoingsGrowthRate: "3.0",
    rentGrowthRate: "3.0",
    interestRateAssumed: "",
  };
}

// ============================================================================
// NORMALIZE helpers
// ============================================================================

function normalizeForForm(projection: any, suggestedYear: number): CashflowFormData {
  return {
    projectionYear: projection.projectionYear ?? suggestedYear,
    periodStartDate: projection.periodStartDate || "",
    periodEndDate: projection.periodEndDate || "",
    grossRentalIncome: projection.grossRentalIncome?.toString() || "",
    otherIncome: projection.otherIncome?.toString() || "",
    vacancyAllowance: projection.vacancyAllowance?.toString() || "",
    councilRates: projection.councilRates?.toString() || "",
    waterRates: projection.waterRates?.toString() || "",
    insurance: projection.insurance?.toString() || "",
    landTax: projection.landTax?.toString() || "",
    bodyCorporate: projection.bodyCorporate?.toString() || "",
    managementFees: projection.managementFees?.toString() || "",
    repairsMaintenance: projection.repairsMaintenance?.toString() || "",
    otherOutgoings: projection.otherOutgoings?.toString() || "",
    capex: projection.capex?.toString() || "",
    outgoingsGrowthRate: projection.outgoingsGrowthRate?.toString() || "3.0",
    rentGrowthRate: projection.rentGrowthRate?.toString() || "3.0",
    interestRateAssumed: projection.interestRateAssumed?.toString() || "",
  };
}

function buildPayload(data: CashflowFormData): Record<string, unknown> {
  const orNull = (v: string | null | undefined) =>
    v && v.trim() !== "" ? v : null;

  return {
    projectionYear: data.projectionYear,
    periodStartDate: data.periodStartDate,
    periodEndDate: data.periodEndDate,
    grossRentalIncome: data.grossRentalIncome,
    otherIncome: orNull(data.otherIncome),
    vacancyAllowance: orNull(data.vacancyAllowance),
    councilRates: orNull(data.councilRates),
    waterRates: orNull(data.waterRates),
    insurance: orNull(data.insurance),
    landTax: orNull(data.landTax),
    bodyCorporate: orNull(data.bodyCorporate),
    managementFees: orNull(data.managementFees),
    repairsMaintenance: orNull(data.repairsMaintenance),
    otherOutgoings: orNull(data.otherOutgoings),
    capex: orNull(data.capex),
    outgoingsGrowthRate: orNull(data.outgoingsGrowthRate),
    rentGrowthRate: orNull(data.rentGrowthRate),
    interestRateAssumed: orNull(data.interestRateAssumed),
  };
}

// ============================================================================
// COMPONENT
// ============================================================================

export function CashflowFormModal({
  open,
  onOpenChange,
  securityId,
  existingProjection,
  suggestedYear = 1,
}: CashflowFormModalProps) {
  const isEdit = !!existingProjection;
  const { toast } = useToast();

  const form = useForm<CashflowFormData>({
    resolver: zodResolver(cashflowFormSchema),
    defaultValues: buildDefaultValues(suggestedYear),
  });

  // Reset form when modal opens or projection data changes
  useEffect(() => {
    if (open) {
      if (existingProjection) {
        form.reset(normalizeForForm(existingProjection, suggestedYear));
      } else {
        form.reset(buildDefaultValues(suggestedYear));
      }
    }
  }, [open, existingProjection, suggestedYear, form]);

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      form.reset(buildDefaultValues(suggestedYear));
    }
    onOpenChange(newOpen);
  };

  const mutation = useMutation({
    mutationFn: async (data: CashflowFormData) => {
      const payload = buildPayload(data);

      if (isEdit) {
        const response = await apiRequest(
          "PATCH",
          `/api/crm/securities/${securityId}/property/cashflow/${existingProjection.id}`,
          payload
        );
        if (!response.ok) {
          const error = await response.text();
          throw new Error(error || "Failed to update cashflow projection");
        }
        return response.json();
      } else {
        const response = await apiRequest(
          "POST",
          `/api/crm/securities/${securityId}/property/cashflow`,
          payload
        );
        if (!response.ok) {
          const error = await response.text();
          throw new Error(error || "Failed to create cashflow projection");
        }
        return response.json();
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/crm/securities", securityId, "property", "cashflow"],
      });
      toast({
        title: "Success",
        description: isEdit
          ? "Cashflow projection updated successfully"
          : "Cashflow projection added successfully",
      });
      handleOpenChange(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: CashflowFormData) => {
    mutation.mutate(data);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="cashflow-form-modal"
      >
        <DialogHeader>
          <DialogTitle>
            {isEdit ? "Edit Cashflow Projection" : "Add Cashflow Projection"}
          </DialogTitle>
          <DialogDescription>
            {isEdit
              ? "Update the NOI projection for this year"
              : "Add a new annual NOI projection for this property"}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">

            {/* Period Details */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                Period Details
              </h3>
              <div className="grid grid-cols-3 gap-4">
                <FormField
                  control={form.control}
                  name="projectionYear"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Projection Year *</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min={1}
                          max={10}
                          placeholder="1"
                          data-testid="input-projection-year"
                          {...field}
                          value={field.value ?? ""}
                          onChange={(e) =>
                            field.onChange(
                              e.target.value ? parseInt(e.target.value, 10) : undefined
                            )
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="periodStartDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Period Start Date *</FormLabel>
                      <FormControl>
                        <Input
                          type="date"
                          data-testid="input-period-start-date"
                          {...field}
                          value={field.value || ""}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="periodEndDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Period End Date *</FormLabel>
                      <FormControl>
                        <Input
                          type="date"
                          data-testid="input-period-end-date"
                          {...field}
                          value={field.value || ""}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            {/* Income */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                Income
              </h3>
              <div className="grid grid-cols-3 gap-4">
                <FormField
                  control={form.control}
                  name="grossRentalIncome"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Gross Rental Income (AUD) *</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="0.00"
                          data-testid="input-gross-rental-income"
                          {...field}
                          value={field.value || ""}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="otherIncome"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Other Income (AUD)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="0.00"
                          data-testid="input-other-income"
                          {...field}
                          value={field.value || ""}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="vacancyAllowance"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Vacancy Allowance (AUD)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="0.00"
                          data-testid="input-vacancy-allowance"
                          {...field}
                          value={field.value || ""}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            {/* Outgoings */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                Outgoings
              </h3>
              <div className="grid grid-cols-3 gap-4">
                <FormField
                  control={form.control}
                  name="councilRates"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Council Rates (AUD)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="0.00"
                          data-testid="input-council-rates"
                          {...field}
                          value={field.value || ""}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="waterRates"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Water Rates (AUD)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="0.00"
                          data-testid="input-water-rates"
                          {...field}
                          value={field.value || ""}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="insurance"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Insurance (AUD)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="0.00"
                          data-testid="input-insurance"
                          {...field}
                          value={field.value || ""}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="landTax"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Land Tax (AUD)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="0.00"
                          data-testid="input-land-tax"
                          {...field}
                          value={field.value || ""}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="bodyCorporate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Body Corporate (AUD)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="0.00"
                          data-testid="input-body-corporate"
                          {...field}
                          value={field.value || ""}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="managementFees"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Management Fees (AUD)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="0.00"
                          data-testid="input-management-fees"
                          {...field}
                          value={field.value || ""}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="repairsMaintenance"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Repairs & Maintenance (AUD)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="0.00"
                          data-testid="input-repairs-maintenance"
                          {...field}
                          value={field.value || ""}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="otherOutgoings"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Other Outgoings (AUD)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="0.00"
                          data-testid="input-other-outgoings"
                          {...field}
                          value={field.value || ""}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="capex"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Capex (AUD)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="0.00"
                          data-testid="input-capex"
                          {...field}
                          value={field.value || ""}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            {/* Growth Assumptions */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                Growth Assumptions
              </h3>
              <div className="grid grid-cols-3 gap-4">
                <FormField
                  control={form.control}
                  name="rentGrowthRate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Rent Growth Rate (%)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="3.00"
                          data-testid="input-rent-growth-rate"
                          {...field}
                          value={field.value || ""}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="outgoingsGrowthRate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Outgoings Growth Rate (%)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="3.00"
                          data-testid="input-outgoings-growth-rate"
                          {...field}
                          value={field.value || ""}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="interestRateAssumed"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Interest Rate Assumed (%)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.0001"
                          placeholder="0.0000"
                          data-testid="input-interest-rate-assumed"
                          {...field}
                          value={field.value || ""}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                data-testid="button-cancel"
                onClick={() => handleOpenChange(false)}
                disabled={mutation.isPending}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                data-testid="button-save"
                disabled={mutation.isPending}
              >
                {mutation.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                {isEdit ? "Update Projection" : "Save Projection"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
